import React, { FormEvent, useState } from 'react'
import { Box, FormHelperText, Grid, Typography, useTheme } from '@material-ui/core'
import clsx from 'clsx'
import { observer } from 'mobx-react-lite'
import { useSettingsPageStyles } from '../hooks/settingsPageStylesHook'
import { NotificationSubject } from '../server/mpsklad_core/Services/Notification/Enums/NotificationSubject'
import { UserAuthModel } from '../server/mpsklad_core/Models/UserAuthModel'
import { useApi } from '../hooks/storeHook'
import { CircleCheckbox } from './CircleCheckbox'
import { LabeledInput } from './LabeledInput'
import { useMessages } from '../hooks/snackbarHooks'
import { dateUtils, formatDateOnly } from '../common/dateTimeUtility'
import { pluralize } from '../common/stringUtility'
import { differenceInCalendarDays, fromUnixTime } from 'date-fns'

export type NotificationSettingsFormProps = {
  user: UserAuthModel
}

export const NotificationSettingsForm =
  observer(({user}: NotificationSettingsFormProps) => {
    const {palette} = useTheme()
    const classes = useSettingsPageStyles()
    const api = useApi()
    const {showSuccess} = useMessages()

    const [email, setEmail] = useState(user.notificationEmail || user.email)
    const [subjects, setSubjects] = useState(user.notificationSubjects)
    const [isLoading, setIsLoading] = useState(false)

    const getSubscriptionInfo = () => {
      if (user.moySkladAppState || !user.subscriptionEndDate) {
        return null
      }

      const endDate = fromUnixTime(user.subscriptionEndDate)
      const diffDays = differenceInCalendarDays(endDate, dateUtils.now)

      return {
        endDate: formatDateOnly(endDate),
        isExpired: endDate < dateUtils.now,
        daysRemaining: diffDays,
        backgroundColor: diffDays < 7
                         ? palette.error.main
                         : diffDays <= 14
                           ? palette.warning.main
                           : palette.mpSkladSecondary.lightGray
      }
    }

    const subscriptionInfo = getSubscriptionInfo()

    const notificationOptions = [
      {value: NotificationSubject.AcountSyncOff, label: 'Отключилась синхронизация аккаунта'},
      {value: NotificationSubject.ProductsSyncOff, label: 'Отключилась синхронизация товаров'},
      {value: NotificationSubject.MatchingUnset, label: 'Сброшен матчинг товара'},
      {value: NotificationSubject.ApiTokenWillExpireSoon, label: 'Скоро истекает срок действия токена'}
    ]

    if (!user.moySkladAppState) {
      notificationOptions.push({
        value: NotificationSubject.SubscriptionWillExpireSoon,
        label: 'Истекает срок действия подписки MPsklad'
      })
    }

    const handleToggle = (value: NotificationSubject) => {
      setSubjects((prev) =>
        prev.includes(value) ? prev.filter((v) => v !== value)
                             : [...prev, value])
    }

    const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

    const handleSave = async (e: FormEvent) => {
      e.preventDefault()

      if (!isValidEmail(email)) {
        alert('Укажите корректный email')
        return
      }

      setIsLoading(true)

      try {
        await api.userSync.setUserNotificationSettings({email, subjects})
        showSuccess('Настройки уведомлений сохранены')
      } catch (error) {
        console.error('Failed to save notification settings', error)
      } finally {
        setIsLoading(false)
      }
    }

    return (
      <Box>
        <Typography className={classes.header}>Настройки уведомлений</Typography>

        <form onSubmit={handleSave}>
          <Grid container className={clsx(classes.settingsTopic, 'default-border')} direction={'column'}>
            {/* Email */}
            <Grid item xs={12} md={12} className={classes.root}>
              <LabeledInput
                required
                id="notificationEmail"
                placeholder="example@email.com"
                type="email"
                label="На этот адрес мы будем присылать уведомления о важных событиях"
                value={email}
                onChange={setEmail}
              />
            </Grid>

            {/* Уведомления */}
            <Grid item xs={12} md={12} className={classes.root}>
              <FormHelperText className={classes.helperText}>
                <span className={classes.dash}>—</span>
                О чём уведомлять?
              </FormHelperText>

              <Box display="flex" flexDirection="column" mt={1}>
                {notificationOptions.map((option) => (
                  <CircleCheckbox
                    key={option.value}
                    label={option.label}
                    checked={subjects.includes(option.value)}
                    onChange={() => handleToggle(option.value)}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>

          <Box display="flex" marginTop={3}>
            <button
              type="submit"
              className={clsx(classes.saveButton, 'default-button')}
              disabled={isLoading}
            >
              <p>{isLoading ? 'Сохраняем...' : 'Сохранить настройки'}</p>
            </button>
          </Box>
        </form>

        {
          subscriptionInfo &&
          <Box
            mb={3} p={2} style={{
            backgroundColor: subscriptionInfo.backgroundColor,
            borderRadius: '12px',
            padding: '23px',
            marginTop: '20px',
            width: 'fit-content'
          }}>
            <div style={{color: palette.mpSkladSecondary.darkBlue1, fontWeight: 600, fontSize: '12px'}}>
              {
                subscriptionInfo.isExpired
                ? `Подписка истекла ${subscriptionInfo.endDate}`
                : `Подписка активна до ${subscriptionInfo.endDate} (${
                  pluralize(subscriptionInfo.daysRemaining, 'остался $ день', 'осталось $ дня', 'осталось $ дней')})`
              }
            </div>
          </Box>
        }
      </Box>
    )
  })